/**********************************************************/
/** Unit testable helper functions for reports components */
/**********************************************************/

import { getLogger } from '../../utils/general';

const logger = getLogger('src.pages.streams.AllStreams');

/**
 * Parses a stream input URL and extracts multicast address and port from query parameters.
 *
 * @param {string} url - The input URL to parse (e.g., rtsp://.../?mcast_port=1234&mcast_addr=192.0.2.27).
 * @returns {{ port: number|null, addr: string|null }}
 *  An object with the extracted multicast port (number or null) and address (string or null).
 */
export function parseInputUrl(url) {
  const result = { port: null, addr: null };
  if (!url) return result;
  try {
    let parser;
    if (/^https?:/i.test(url)) {
      parser = new URL(url);
    } else {
      // Use anchor fallback for non-http(s) URLs (i.e. rtsp)
      parser = document.createElement('a');
      parser.href = url;
      parser.search = parser.search || '';
    }
    parser.search
      .replace(/^\?/, '')
      .split('&')
      .forEach((token) => {
        if (token.startsWith('mcast_port=')) {
          const portValue = token.split('=')[1];
          const parsedPort = parseInt(portValue, 10);
          result.port = isNaN(parsedPort) ? null : parsedPort;
        } else if (token.startsWith('mcast_addr=')) {
          const addr = token.split('=')[1];
          const ipv4 = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/;
          const match = addr && addr.match(ipv4);
          if (match) result.addr = match[0];
        }
      });
  } catch (e) {
    logger.warn(`Failed to parse input URL: ${url}`, e);
  }
  return result;
}

/**
 * Orchestrator function that validates input, sorts, and maps rows for CSV export data.
 * @param {Array<Object>} filteredData - The filtered data to export.
 * @param {Array<{field: string, headerName: string}>} tableColumns - Table column definitions.
 * @returns {Array<Object>} Array of objects ready for CSV export (header names as keys).
 */
export function formatExportData(filteredData, tableColumns) {
  if (!Array.isArray(filteredData)) {
    logger.warn('Expected filteredData to be an array');
    return [];
  }
  if (!Array.isArray(tableColumns) || !tableColumns.length) {
    logger.warn('Expected tableColumns to be a non-empty array');
    return [];
  }

  const exportFields = _getExportFields(tableColumns);
  const headerMap = _getHeaderMap(tableColumns);
  if (!headerMap || typeof headerMap !== 'object' || Object.keys(headerMap).length === 0) {
    logger.warn('No headerMap could be constructed from tableColumns');
    return [];
  }

  const firstField = exportFields[0];
  const sortedData = _sortDataByFirstField(filteredData, firstField);
  return _mapRowsToExportFormat(sortedData, exportFields, headerMap);
}

/**
 * Maps a single device object to a Camera Metadata report row.
 * Handles both array metadata (API format) and JSON string metadata.
 * Metadata keys 'route', 'direction', and 'mrm' map to routeName, trafficDirection, and mileMarker.
 * @param {Object} device - A device object with optional metadata field.
 * @returns {Object} A row object suitable for the Camera Metadata report table.
 */
export function mapCameraMetadataRow(device) {
  let metadata = {};
  if (Array.isArray(device.metadata)) {
    device.metadata.forEach(({ key, value }) => { metadata[key] = value; });
  } else if (device.metadata) {
    try {
      metadata = JSON.parse(device.metadata);
    } catch (e) {
      logger.warn('Failed to parse metadata for device:', device.id, e);
    }
  }
  return {
    id: device.id,
    name: device.name,
    description: device.description ?? '',
    network_address: device.network_address ?? '',
    connection_type: device.connection_type ?? '',
    latitude: device.latitude ?? '',
    longitude: device.longitude ?? '',
    jurisdiction: metadata['jurisdiction'] ?? '',
    routeName: metadata['route'] ?? '',
    trafficDirection: metadata['direction'] ?? '',
    mileMarker: metadata['mrm'] ?? '',
  };
}

/************************************************************/
/** Private helper functions exported only for unit testing */
/************************************************************/

/**
 * Returns an array of field names from table column definitions.
 * @param {Array<{field: string}>} columns - Table column definitions.
 * @returns {string[]} Array of field names.
 */
export function _getExportFields(columns) {
  return Array.isArray(columns) ? columns.map((col) => col.field) : [];
}

/**
 * Returns a mapping of field names to header names from table column definitions.
 * @param {Array<{field: string, headerName: string}>} columns - Table column definitions.
 * @returns {Object} Mapping of field name to header name.
 */
export function _getHeaderMap(columns) {
  return Array.isArray(columns)
    ? Object.fromEntries(
      columns
        .filter((col) => col.field && col.headerName)
        .map((col) => [col.field, col.headerName])
    )
    : {};
}

/**
 * Sorts an array of data objects by the specified field in ascending order (case-insensitive).
 * @param {Array<Object>} data - Array of data objects to sort.
 * @param {string} firstField - Field name to sort by.
 * @returns {Array<Object>} Sorted array of data objects.
 */
export function _sortDataByFirstField(data, firstField) {
  if (!firstField || typeof firstField !== 'string') {
    logger.warn('Expected firstField to be a non-empty string');
    return [...data];
  }
  return [...data].sort((a, b) => {
    const aVal = a[firstField]?.toString().toLowerCase() || '';
    const bVal = b[firstField]?.toString().toLowerCase() || '';
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
}

/**
 * Maps an array of data objects to a new array using exportFields and headerMap for CSV export.
 * @param {Array<Object>} data - Array of data objects.
 * @param {string[]} exportFields - Array of field names to export.
 * @param {Object} headerMap - Mapping of field name to header name.
 * @returns {Array<Object>} Array of objects with header names as keys.
 */
export function _mapRowsToExportFormat(data, exportFields, headerMap) {
  return data.map((row) => {
    const newRow = {};
    exportFields.forEach((field) => {
      newRow[headerMap[field]] = row[field];
    });
    return newRow;
  });
}
